import React, { useState, useEffect, useCallback } from "react";
import { useDispatch } from "react-redux";
import { Button, Typography, Row, Col, Table, Divider, notification, Input } from "antd";
import OwnerForm from "./OwnerForm";
import axios from "axios";
import "./Owner.css";
// Only import useTranslation if needed elsewhere in the component
import { useTranslation } from 'react-i18next';

const { Title } = Typography;
const { Search } = Input;

const Owners = props => {
  const [currentPageOwners, setCurrentPageOwners] = useState([]);
  const [listState, setListState] = useState({
    pagination: { pageSize: 10, current: 1 },
    filters: {},
    sorter: {}
  });
  const [isLoading, setIsLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [editState, setEditState] = useState({ owner: {}, valid: false });
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedRowKey, setSelectedRowKey] = useState(null);
  // Keep this for translations in column renderers
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const startLoading = () => dispatch({ type: "START_LOADING" });
  const stopLoading = (val) => dispatch({ type: "STOP_LOADING", status: val });

  const handleTableChange = (pagination, filters, sorter) => {
    // Convert antd's sorter object to a format we can use
    const sortConfig = sorter.field ? {
      field: sorter.field,
      order: sorter.order // 'ascend' or 'descend'
    } : {};

    setListState({
      pagination: pagination,
      filters: filters,
      sorter: sortConfig
    });
  };

  const saveOwner = () => {
    // Only save if in edit mode and data is valid
    if (!isEditMode || !editState.valid) {
      return;
    }

    console.log("[OWNER] = " + JSON.stringify(editState));
    startLoading();

    // Determine whether to create a new owner or update an existing one
    const isNewOwner = editState.owner.id === -1;
    const method = isNewOwner ? 'post' : 'put';
    const endpoint = isNewOwner
      ? process.env.REACT_APP_API_ENDPOINT + "/owners/"
      : process.env.REACT_APP_API_ENDPOINT + "/owners/" + editState.owner.id;

    // If creating a new owner, ensure id is null as backend expects
    const ownerData = { ...editState.owner };
    if (isNewOwner) {
      ownerData.id = null;
    }

    axios[method](endpoint, ownerData)
      .then((response) => {
        const savedOwner = response.data;
        // Update the selected row key to match the saved owner
        setSelectedRowKey(savedOwner.id);
        setIsEditMode(false);

        // Reload owners from server, which will also reselect this owner with fresh data
        reloadOwners();

        setTimeout(() => {
          stopLoading("success");
        }, 1000);
      })
      .catch(function (error) {
        console.log(error);
        setTimeout(() => {
          stopLoading("failure");
        }, 1000);
      });
  };

  const newOwner = () => {
    // Only change the page if needed
    if (listState.pagination.current !== 1) {
      // First update pagination to page 1 and let the data reload
      setListState({
        ...listState,
        pagination: {
          ...listState.pagination,
          current: 1
        }
      });

      // After the reload is complete, then create the new owner
      // We use setTimeout to ensure this runs after the page change and data reload
      setTimeout(() => {
        const newOwner = { id: -1 };
        setCurrentPageOwners([newOwner, ...currentPageOwners]);
        setEditState({ owner: newOwner, valid: false });
        setSelectedRowKey(-1);
        setIsEditMode(true);
      }, 500); // Short delay to let the reload complete
    } else {
      // If already on page 1, just create the new owner without changing pages
      const newOwner = { id: -1 };
      setCurrentPageOwners([newOwner, ...currentPageOwners]);
      setEditState({ owner: newOwner, valid: false });
      setSelectedRowKey(-1);
      setIsEditMode(true);
    }
  };

  const cancelEdit = () => {
    // If we're canceling a new owner, remove it from the temporary display
    if (editState.owner.id === -1) {
      // First remove the temporary new owner from the current page
      setCurrentPageOwners(currentPageOwners.filter(owner => owner.id !== -1));
      setSelectedRowKey(null);
      setEditState({ owner: {}, valid: false });
    } else {
      // If we're canceling an edit to an existing owner, remember its ID
      const ownerId = editState.owner.id;
      // We'll reselect this owner after reloading
      setSelectedRowKey(ownerId);
    }

    setIsEditMode(false);

    // Reload the data from the server in all cases
    // This ensures we have the latest data and proper selection state
    reloadOwners();
  };

  const editOwner = id => {
    const toEdit = currentPageOwners.find(i => i.id === id);
    // Create a deep copy to avoid reference issues
    const toEditCopy = JSON.parse(JSON.stringify(toEdit));
    setEditState({ owner: toEditCopy, valid: true });
    setIsEditMode(true);
  };

  const deleteOwner = id => {
    const toDelete = currentPageOwners.find(i => i.id === id);
    startLoading();

    // Use the new RESTful endpoint for delete
    axios
      .delete(`${process.env.REACT_APP_API_ENDPOINT}/owners/${toDelete.id}`)
      .then((response) => { // ignored response
        reloadOwners();
        setEditState({ owner: {}, valid: false });
        setTimeout(() => {
          stopLoading("success");
        }, 1000);
      })
      .catch(function (error) {
        console.log(error);
        setTimeout(() => {
          stopLoading("failure");
        }, 1000);
      })
      .finally(function () { });
  };

  const handleFullNameSearch = (value) => {
    setListState({
      ...listState,
      pagination: {
        ...listState.pagination,
        current: 1
      },
      filters: {
        ...listState.filters,
        fullName: value ? [value] : undefined
      }
    });
  };

  const handleFiscalCodeSearch = (value) => {
    setListState({
      ...listState,
      pagination: {
        ...listState.pagination,
        current: 1
      },
      filters: {
        ...listState.filters,
        fiscalCode: value ? [value] : undefined
      }
    });
  };

  const ownerChange = (newRecord, valid) => {
    var newOwner = { ...editState };
    // Update the owner object with values from the form
    newOwner.owner.fullName = newRecord.fullName.value;
    newOwner.owner.fiscalCode = newRecord.fiscalCode.value;
    newOwner.owner.email = newRecord.email.value;
    newOwner.owner.phone = newRecord.phone.value;
    newOwner.valid = valid;
    setEditState(newOwner);
  };

  const reloadOwners = useCallback(() => {
    // Memorize the currently selected owner ID before reloading
    const currentSelectedId = selectedRowKey;

    setIsLoading(true);
    axios
      .get(process.env.REACT_APP_API_ENDPOINT + "/owners/", {
        params: {
          page: listState.pagination.current - 1,  // Spring uses 0-based page indexing
          size: listState.pagination.pageSize,
          // Convert the sort parameters to Spring format
          sort: listState.sorter.field ?
            `${listState.sorter.field},${listState.sorter.order === 'descend' ? 'desc' : 'asc'}` :
            undefined,
          // Pass individual filter parameters
          fullName: listState.filters.fullName?.[0],
          fiscalCode: listState.filters.fiscalCode?.[0],
          email: listState.filters.email?.[0],
          phone: listState.filters.phone?.[0]
        }
      })
      .then((response) => {
        // Use response.data.content for the page data
        const newOwners = response.data.content;
        setCurrentPageOwners(newOwners);
        // Use response.data.totalElements for the total count
        setTotalCount(response.data.totalElements);

        // If there was a selected owner, find it in the new data and update editState
        if (currentSelectedId !== null && currentSelectedId !== -1) {
          const selectedOwner = newOwners.find(owner => owner.id === currentSelectedId);
          if (selectedOwner) {
            setEditState({ owner: selectedOwner, valid: true });
            setSelectedRowKey(currentSelectedId);
          }
        }
      })
      .catch(function (error) {
        console.log(error);
      })
      .finally(function () {
        setIsLoading(false);
      });
  }, [listState, selectedRowKey]); // Added dependencies

  const importOwners = () => {
    notification.open({
      placement: "bottomRight",
      message: 'RETRIEVE DATA FROM EXTERNAL DATABASES ',
      description:
        'Function currently not available.',
    });
  };

  useEffect(() => {
    reloadOwners();
  }, [listState, reloadOwners]); // Added reloadOwners to dependencies

  const showOwner = id => {
    const toShow = currentPageOwners.find(i => i.id === id);
    setEditState({ owner: toShow, valid: true });
    setIsEditMode(false);
  };

  const columns = [
    {
      title: "Full Name",
      dataIndex: "fullName",
      width: "30%"
    },
    {
      title: "Fiscal Code",
      dataIndex: "fiscalCode",
      width: "20%"
    },
    {
      title: "Email",
      dataIndex: "email",
      width: "20%"
    },
    {
      title: "Phone",
      dataIndex: "phone",
      width: "15%"
    },
    {
      title: "Action",
      key: "action",
      width: "15%",
      render: (text, record) => (
        <span>
          <Button
            type="default"
            size="small"
            disabled={isEditMode}
            onClick={(e) => {
              e.stopPropagation(); // Stop the click event from propagating to parent elements
              editOwner(record.id);
            }}
          >
            Edit
          </Button>
          <Divider type="vertical" />
          <Button
            type="default"
            size="small"
            disabled={isEditMode}
            onClick={() => deleteOwner(record.id)}
          >
            Delete
          </Button>
        </span>
      )
    }
  ];

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={3}>Owners</Title>
        </Col>
        <Col>
          <Button key="1"
            disabled={isEditMode}
            onClick={() => reloadOwners()}>
            Refresh
          </Button>
          <Button key="2"
            style={{ marginLeft: 8 }}
            disabled={isEditMode}
            onClick={() => newOwner()}>
            New
          </Button>
          <Button
            key="3"
            style={{ marginLeft: 8 }}
            type="primary"
            disabled={!isEditMode || !editState.valid}
            onClick={() => saveOwner()}>
            Save
          </Button>
          <Button
            key="5"
            style={{ marginLeft: 8 }}
            onClick={() => cancelEdit()}
            disabled={!isEditMode}>
            Cancel
          </Button>
          <Button
            key="4"
            style={{ marginLeft: 8 }}
            disabled={isEditMode}
            onClick={() => importOwners()}>
            Import
          </Button>
        </Col>
      </Row>
      <Row style={{ marginBottom: 16 }}>
        <Col span={8}>
          <Search
            placeholder="Search by full name"
            allowClear
            onSearch={handleFullNameSearch}
            style={{ width: '100%' }}
            disabled={isEditMode}
          />
        </Col>
        <Col span={8} offset={1}>
          <Search
            placeholder="Search by fiscal code"
            allowClear
            onSearch={handleFiscalCodeSearch}
            style={{ width: '100%' }}
            disabled={isEditMode}
          />
        </Col>
      </Row>
      <Row gutter={[16, 16]}>
        <Col span={24} style={{ height: 250 }}>
          <Table
            size="small"
            scroll={{ y: 150 }}
            columns={columns}
            rowKey={owner => owner.id}
            dataSource={currentPageOwners}
            pagination={{
              pageSize: listState.pagination.pageSize,
              current: listState.pagination.current,
              total: totalCount,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} owners`,
              onChange: (page, pageSize) => {
                setListState({
                  ...listState,
                  pagination: {
                    ...listState.pagination,
                    current: page,
                    pageSize: pageSize
                  }
                });
              }
            }}
            loading={isLoading}
            onChange={(pagination, filters, sorter) => handleTableChange(pagination, filters, sorter)}
            onRow={(record) => ({
              onClick: () => {
                if (!isEditMode) {
                  showOwner(record.id);
                  setSelectedRowKey(record.id);
                }
              },
              className: record.id === selectedRowKey ? 'selected-row' : '',
              style: isEditMode ? { cursor: 'not-allowed' } : {}
            })}
            rowClassName={(owner) => owner.id === selectedRowKey ? 'selected-row' : ''}
          />
        </Col>
      </Row>
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <OwnerForm
            record={editState}
            parentCallback={ownerChange}
            isEditMode={isEditMode}
          />
        </Col>
      </Row>
    </div>
  );
};

export default Owners;